import { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  SIZE, COLS, ROW_LABELS, COLUMN_LABELS, idx,
} from './spriteData';

const THUMB = 2; // on-screen px per art pixel

function Thumb({
  pixels, active, onSelect, label,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const ctx = ref.current.getContext('2d');
    ctx.clearRect(0, 0, SIZE * THUMB, SIZE * THUMB);
    for (let y = 0; y < SIZE; y += 1) {
      for (let x = 0; x < SIZE; x += 1) {
        const c = pixels[idx(x, y)];
        if (c) {
          ctx.fillStyle = c;
          ctx.fillRect(x * THUMB, y * THUMB, THUMB, THUMB);
        }
      }
    }
  }, [pixels]);

  return (
    <button
      type="button"
      onClick={onSelect}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={`rounded border-2 bg-gray-900 transition-colors ${
        active ? 'border-primary' : 'border-gray-700 hover:border-gray-500'
      }`}
    >
      <canvas
        ref={ref}
        width={SIZE * THUMB}
        height={SIZE * THUMB}
        style={{ imageRendering: 'pixelated', display: 'block' }}
      />
    </button>
  );
}

Thumb.propTypes = {
  pixels: PropTypes.arrayOf(PropTypes.string).isRequired,
  active: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

/**
 * All 16 frames laid out exactly as they sit in the exported sheet — rows are
 * facings, columns are the walk cycle. Mirroring the sheet layout means the
 * thing you edit looks like the thing you ship.
 */
export default function FrameStrip({
  frames, active, onSelect, onClear,
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Frames
        </h3>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-red-400 hover:text-red-300"
        >
          Clear frame
        </button>
      </div>

      <div className="inline-block">
        <div className="grid grid-cols-[auto_repeat(4,minmax(0,1fr))] gap-1 items-center">
          <div />
          {COLUMN_LABELS.map((label) => (
            <div key={label} className="text-[10px] text-gray-500 text-center">
              {label}
            </div>
          ))}

          {ROW_LABELS.map((facing, row) => (
            <Fragment key={facing}>
              <div className="text-[10px] text-gray-500 pr-1 text-right">
                {facing}
              </div>
              {Array.from({ length: COLS }, (_, col) => {
                const i = row * COLS + col;
                return (
                  <Thumb
                    key={i}
                    pixels={frames[i]}
                    active={active === i}
                    onSelect={() => onSelect(i)}
                    label={`${facing} ${COLUMN_LABELS[col]}`}
                  />
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      <p className="text-[10px] text-gray-500 max-w-xs">
        Walk cycles play
        {' '}
        <strong>idle → step L → idle → step R</strong>
        , so the &quot;spare&quot; column is unused by default.
      </p>
    </div>
  );
}

FrameStrip.propTypes = {
  frames: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  active: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};
